import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import { UserDetails } from "../../../helpers/types/common";
import { getValueOrBlank, validateApiParamValue } from "../../../helpers/utils";
import { ParticipantActivityResponse } from "../../../pages/createProgram/modelsAndSchemas/Models";
import apiInstance from "../../instances/apiInstance";
import { GetClientListResponse } from "../client/models";
import { ParticipantActivityParams } from "../program/models";
import {
  CreateNISParticipantRequestPayload,
  GetClientListByCountryParams,
  NISParticipantCreatedResponse,
  NisParticipantResponse,
  NISParticipantStatusUpdatePayload,
  NISStatusList,
  UpdateNISParticipantRequestPayload,
} from "./models";

export const useGetProjectManagersListMutation = () => {
  return useMutation({
    mutationFn: async ({
      clientId,
    }: {
      clientId: string;
    }): Promise<UserDetails[]> => {
      const res = await apiInstance.get<UserDetails[]>(
        `${API_ENDPOINTS.GET_PROJECT_MANAGERS_FOR_NIS_PARTICIPANT}?clientId=${clientId}`,
      );
      return res.data;
    },
  });
};

export const useGetClientListByCountry = () => {
  const [params, setParams] = useState<GetClientListByCountryParams>();

  const query = useQuery({
    queryKey: ["getClientList", params],
    queryFn: async (): Promise<GetClientListResponse[]> => {
      if (!params) {
        return [];
      }
      const { countryCode = "", isActiveClientOnly } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_CLIENT_LIST_BY_COUNTRY(
          countryCode,
          isActiveClientOnly,
        ),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: GetClientListByCountryParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetNISParticipantDetailsById = (nisParticipantId?: string) => {
  return useQuery({
    queryKey: ["nisParticipant", nisParticipantId],
    queryFn: async (): Promise<NisParticipantResponse> => {
      if (!nisParticipantId) {
        return {} as NisParticipantResponse;
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getNISParticipantById(
          validateApiParamValue(nisParticipantId),
        ),
      );
      return res.data;
    },
    enabled: !!nisParticipantId,
    retry: false,
  });
};

export const useGetNISParticipantStatusById = () => {
  return useMutation({
    mutationFn: async (nisParticipantId: string): Promise<NISStatusList[]> => {
      if (!nisParticipantId) {
        return [] as NISStatusList[];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getNISParticipantStatusById(nisParticipantId),
      );
      return res.data;
    },
  });
};

export const useCreateNISParticipant = () => {
  return useMutation({
    mutationFn: async (
      params: CreateNISParticipantRequestPayload,
    ): Promise<NISParticipantCreatedResponse> => {
      const res = await apiInstance.post(
        API_ENDPOINTS.CREATE_NIS_PARTICIPANT,
        params,
      );
      return res.data;
    },
  });
};

export const useUpdateNISParticipant = () => {
  return useMutation({
    mutationFn: async (params: UpdateNISParticipantRequestPayload) => {
      const res = await apiInstance.put(
        API_ENDPOINTS.updateNISParticipantById(params.nisParticipantId),
        params,
      );
      return res.data;
    },
  });
};

export const useUpdateNISParticipantStatus = () => {
  return useMutation({
    mutationFn: async (
      params: NISParticipantStatusUpdatePayload,
    ): Promise<NISParticipantCreatedResponse> => {
      const res = await apiInstance.post(
        API_ENDPOINTS.updateNISParticipantStatusById(params.nisParticipantId),
        params,
      );
      return res.data;
    },
  });
};

export const useGetNISParticipantActivitiesList = () => {
  const [nisParticipantId, setNisParticipantId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetNISParticipantActivitiesList", nisParticipantId],
    queryFn: async (): Promise<ParticipantActivityResponse[]> => {
      if (!nisParticipantId) {
        return [] as ParticipantActivityResponse[];
      }
      let url = API_ENDPOINTS.getNISParticipantActivityTimelineById(
        nisParticipantId ?? "",
      );
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!nisParticipantId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (id: string) => {
    setNisParticipantId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useAddNISParticipantActivity = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: ParticipantActivityParams;
      programId: string;
    }) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.addNISParticipantActivityTimelineById(
          getValueOrBlank(programId), // nisId is passed in programId
        ),
        params,
      );
      return res.data;
    },
  });
};

export const useUpdateNISParticipantActivity = () => {
  return useMutation({
    mutationFn: async ({
      params,
      participantActivityTimeLineId,
    }: {
      params: ParticipantActivityParams;
      participantActivityTimeLineId: string;
    }) => {
      const res = await apiInstance.put(
        API_ENDPOINTS.updateNISParticipantActivityTimelineById(
          participantActivityTimeLineId,
        ),
        params,
      );
      return res.data;
    },
  });
};

export const useDeleteNISParticipantActivityTimeLineMutation = () => {
  return useMutation({
    mutationFn: (timelineActivityId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteNISActivityFromTimeline(timelineActivityId)}`,
      );
    },
  });
};

export const useGetActivityTypesListForNIS = () => {
  const [nisParticipantStatus, setNisParticipantStatus] = useState<string>();

  const query = useQuery({
    queryKey: ["useActivityTypesListForNIS", nisParticipantStatus],
    queryFn: async () => {
      if (!nisParticipantStatus) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getActivityTypeListForNIS(nisParticipantStatus),
      );
      return res.data;
    },
    enabled: !!nisParticipantStatus, // Only run when nisParticipantStatus is set
    retry: false,
  });

  const refetchWithParams = (nisParticipantStatus: string) => {
    setNisParticipantStatus(nisParticipantStatus);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetModalityByIdForNIS = () => {
  const [activityTypeKey, setActivityTypeKey] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetModalityByIdForNIS", activityTypeKey],
    queryFn: async () => {
      if (!activityTypeKey) {
        return [];
      }

      const res = await apiInstance.get(
        API_ENDPOINTS.getModalityListForNIS(activityTypeKey),
      );
      return res.data;
    },
    enabled: !!activityTypeKey,
    retry: false,
  });

  const refetchWithParams = (activityTypeKey: string) => {
    setActivityTypeKey(activityTypeKey);
    query.refetch();
  };

  const clearListOfModalityData = () => {
    setActivityTypeKey("");
  };

  return { ...query, refetchWithParams, clearListOfModalityData };
};

export const useGetTopicListForNIS = () => {
  const [params, setParams] = useState<{
    activityTypeId?: string;
    nisParticipantStatus?: string;
  }>();

  const query = useQuery({
    queryKey: ["useGetTopicListForNIS", params],
    queryFn: async () => {
      if (!params?.activityTypeId || !params?.nisParticipantStatus) {
        return [];
      }

      const url = `${API_ENDPOINTS.getTopicListForNIS(params.activityTypeId, params.nisParticipantStatus)}`;
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!params?.activityTypeId && !!params?.nisParticipantStatus,
    retry: false,
  });

  const refetchWithParams = (activityType: string, status: string) => {
    setParams({ activityTypeId: activityType, nisParticipantStatus: status });
    query.refetch();
  };

  const clearListOfTopicData = () => {
    setParams(undefined);
  };

  return { ...query, refetchWithParams, clearListOfTopicData };
};
